import { useState, useMemo, useCallback, useEffect } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { UserRoles } from './UserRoles';
import {
  UserDetailsQuery,
  useGetDirectlyAssignedUserPermissionsQuery,
  useGetUserPermissionsByRolesQuery,
  usePermissionsByRoleLazyQuery,
  PermissionsByRoleQuery,
} from '@/__generated__/graphql';
import UserPermissions from './UserPermissions';
import { getPermissionsWithRoles } from './utils';
import { EquipmentTabSkeleton } from '@/components/Analysis/shared/EquipmentsTab/EquipmentTabSkeleton';
import { AccordianToggleButton } from '../styles';
import { NOT_APPLICABLE_LABEL } from '@/constants';

type Props = {
  user: UserDetailsQuery['userDetails']['user'];
  userId: string;
};
type Permission = PermissionsByRoleQuery['permissionsByRole'][0];

export const RolesAndPermission = ({ user, userId }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [allRolePermissions, setAllRolePermissions] = useState<Permission[]>([]);
  const roles = user.roleIds.map((role) => role?.name ?? NOT_APPLICABLE_LABEL);
  const [selectedRole, setSelectedRole] = useState(roles[0]);
  const { data } = useGetDirectlyAssignedUserPermissionsQuery({
    variables: { userId },
  });
  const directlyAssignedPermissions = useMemo(
    () =>
      data?.getDirectlyAssignedUserPermissions?.reduce<string[]>((accumulator, permission) => {
        if (permission?.name) {
          accumulator.push(permission.name);
        }
        return accumulator;
      }, []) || [],
    [data]
  );

  const [getPermissions, { data: allPermissionsByRole }] = usePermissionsByRoleLazyQuery();

  useEffect(() => {
    if (selectedRole) {
      getPermissions({
        variables: {
          roleId: user.roleIds.find((role) => role?.name === selectedRole)?.auth0Id ?? '',
        },
      });
    }
  }, [selectedRole, getPermissions, user.roleIds]);

  useEffect(() => {
    setAllRolePermissions(allPermissionsByRole?.permissionsByRole ?? []);
  }, [allPermissionsByRole?.permissionsByRole]);

  const { data: permissionsByRolesData, loading } = useGetUserPermissionsByRolesQuery({
    variables: { userId },
    fetchPolicy: 'cache-first',
  });

  const allPermissions = useMemo(() => {
    return allRolePermissions.map((rolePermission) => ({
      name: rolePermission.key,
      description: rolePermission.description,
    }));
  }, [allRolePermissions]);

  const permissions = getPermissionsWithRoles(permissionsByRolesData, allPermissions, selectedRole);

  const handleSelectedRole = useCallback((role: string) => {
    setSelectedRole(role);
  }, []);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        '&::before': {
          opacity: 0,
        },
        width: '100%',
        backgroundColor: '#6e819810',
        padding: '1%',
        borderRadius: '10px',
      }}
    >
      <AccordionSummary
        expandIcon={
          <AccordianToggleButton
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          >
            <ArrowDropDown sx={{ transform: `rotate(${isExpanded ? '180deg' : '0deg'})` }} />
          </AccordianToggleButton>
        }
      >
        <Typography fontSize={16} fontWeight={700} mb={5}>
          Roles and Permissions
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2 }}>
        <Typography fontSize={16} fontWeight={700} mb={1}>
          Roles
        </Typography>

        <UserRoles
          roles={roles}
          selectedRole={selectedRole}
          handleSelectedRole={handleSelectedRole}
        />

        {user.roleIds.length > 0 && (
          <>
            <Typography fontSize={16} fontWeight={700} mt={5} mb={1}>
              Permissions
            </Typography>

            {loading && <EquipmentTabSkeleton />}

            {!loading && (
              <UserPermissions
                permissions={permissions}
                directPermissions={directlyAssignedPermissions}
              />
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
